/**
 * Print PDF in an office's own format: one filled-in page image per sheet of
 * paper, through the same hidden frame and print dialog as the standard print
 * (../printDocument.js). That frame only waits for fonts, so here every page
 * image is decoded before the dialog opens — otherwise the preview can show a
 * blank page.
 */
import { canvasToPngBlob } from "../breakdownImage";
import { escapeHtml, mountHiddenFrame } from "../printDocument";

// As long as the standard print's: the preview is modal, and removing the frame
// underneath it would blank it.
const FRAME_LIFETIME_MS = 10 * 60 * 1000;

export function buildFormatPrintHtml(urls, { title = "Insurance Breakdown", landscape = false } = {}) {
  const [w, h] = landscape ? ["10.5in", "8in"] : ["8in", "10.5in"];
  const css =
    `@page{size:letter ${landscape ? "landscape" : "portrait"};margin:0.25in}` +
    "html,body{margin:0;padding:0;background:#fff}" +
    `.page{width:${w};height:${h};display:flex;align-items:flex-start;justify-content:center;` +
    "overflow:hidden;break-after:page;page-break-after:always}" +
    ".page:last-child{break-after:auto;page-break-after:auto}" +
    ".page img{max-width:100%;max-height:100%;object-fit:contain}";
  const pages = urls.map((u, i) => `<div class="page"><img src="${escapeHtml(u)}" alt="Page ${i + 1}"/></div>`);
  return (
    "<!DOCTYPE html><html><head><meta charset=\"utf-8\"/>" +
    `<title>${escapeHtml(title)}</title><style>${css}</style></head><body>${pages.join("")}</body></html>`
  );
}

function decodeAll(doc) {
  const images = Array.prototype.slice.call((doc && doc.images) || []);
  return Promise.all(images.map((img) => {
    if (img && typeof img.decode === "function") return img.decode().catch(() => null);
    if (!img || img.complete) return null;
    return new Promise((resolve) => {
      img.onload = resolve;
      img.onerror = resolve;
    });
  }));
}

/** Open the print dialog for the filled-in pages (canvases from paintPages). */
export async function printFormatPages(canvases, { title } = {}) {
  const blobs = await Promise.all(canvases.map((c) => canvasToPngBlob(c)));
  const urls = blobs.map((b) => URL.createObjectURL(b));
  const landscape = canvases.length > 0 && canvases[0].width > canvases[0].height;
  let frame;
  try {
    frame = await mountHiddenFrame(buildFormatPrintHtml(urls, { title, landscape }));
  } catch (err) {
    urls.forEach((u) => URL.revokeObjectURL(u));
    throw err;
  }
  const { win, doc, cleanup } = frame;
  let timer = null;
  const finish = () => {
    if (timer) clearTimeout(timer);
    timer = null;
    cleanup();
    urls.forEach((u) => URL.revokeObjectURL(u));
  };
  try {
    await decodeAll(doc);
    win.addEventListener("afterprint", finish);
    timer = setTimeout(finish, FRAME_LIFETIME_MS);
    win.focus();
    win.print();
  } catch (err) {
    finish();
    throw err;
  }
}
